import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import { CreateDiscountModal } from "@/pages/stammdaten/CreateDiscountModal";
import { EditDiscountModal } from "@/pages/stammdaten/EditDiscountModal";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import { formatIsoDateShort } from "@/lib/bookingDates";
import type { Discount } from "@/types/camping";

const TYPE_LABELS: Record<string, string> = {
  percent_total: "Prozent auf Gesamt",
  percent_category: "Prozent auf Artikelkategorie",
  fixed: "Fixbetrag",
};

export function DiscountsPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWrite = canWriteStammdaten(groups);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Discount | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<Discount[]>("/api/v1/discounts?include_inactive=true")
      .then(setDiscounts)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({ title: "Stammdaten", subtitle: "Rabatte" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-label-secondary">
        Rabatte werden bei der Rechnungserstellung als negative Position gebucht und sind im
        Audit-Protokoll nachvollziehbar.
      </p>

      <EditDiscountModal
        open={!!selected}
        discount={selected}
        onClose={() => setSelected(null)}
        onSaved={load}
        canEdit={canWrite}
      />
      <CreateDiscountModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />

      {!canWrite ? <StammdatenWriteHint /> : null}

      {canWrite ? (
        <div className="flex justify-end">
          <Button type="button" className="w-full sm:w-auto" onClick={() => setCreateOpen(true)}>
            Neuer Rabatt
          </Button>
        </div>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <Table wide emptyMessage="Keine Rabatte." isEmpty={discounts.length === 0}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Art</th>
              <th>Wert</th>
              <th>Gültigkeit</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {discounts.map((d) => (
              <ClickableTableRow
                key={d.id}
                ariaLabel={`Rabatt ${d.name} öffnen`}
                onActivate={() => setSelected(d)}
              >
                <td className="font-medium">{d.name}</td>
                <td>{TYPE_LABELS[d.discount_type] ?? d.discount_type}</td>
                <td className="tabular-nums">
                  {d.discount_type === "fixed" ? `${d.value} EUR` : `${d.value} %`}
                </td>
                <td className="text-sm text-label-secondary">
                  {d.valid_from || d.valid_until
                    ? `${d.valid_from ? formatIsoDateShort(d.valid_from) : "…"} – ${d.valid_until ? formatIsoDateShort(d.valid_until) : "…"}`
                    : "unbegrenzt"}
                </td>
                <td>{d.is_active ? "Aktiv" : "Inaktiv"}</td>
              </ClickableTableRow>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
